import React from 'react';
import { Container, Typography, Button, Box, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import PageTransition from '../components/animations/PageTransition';

const Home: React.FC = () => {
    const theme = useTheme();
    const isLandscape = useMediaQuery('(orientation: landscape)');
    const isMax1023 = useMediaQuery('(max-width:1023px)');
    const isMax599 = useMediaQuery(theme.breakpoints.down('sm')); // <600
    const isMax1199 = useMediaQuery('(max-width:1199px)');

    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [bgPos, setBgPos] = React.useState<string>('center center');

    // Decide which asset is active so we can use the right focal point
    const currentImage = React.useMemo(() => {
        if (isLandscape && isMax1023) return '/jcfStandingLanding-mobile-landscape.jpg';
        if (isMax599) return '/jcfStandingLanding-mobile.jpg';
        if (isMax1199) return '/jcfStandingLanding-tablet.jpg';
        return '/jcfStandingLanding-desktop.jpg';
    }, [isLandscape, isMax1023, isMax599, isMax1199]);

    const focal = React.useMemo(() => {
        switch (currentImage) {
            case '/jcfStandingLanding-mobile.jpg':
                return { x: 0.56, y: 0.20 };
            case '/jcfStandingLanding-tablet.jpg':
                return { x: 0.41, y: 0.20 };
            case '/jcfStandingLanding-mobile-landscape.jpg':
                return { x: 0.20, y: 0.32 }; // approx for mobile landscape
            default:
                return { x: 0.29, y: 0.23 }; // desktop
        }
    }, [currentImage]);

    // Place the focal point around top-left third to keep body below visible
    const target = { x: 0.30, y: 0.30 };

    React.useEffect(() => {
        let imgEl: HTMLImageElement | null = null;
        let raf = 0;

        const recalc = (iw: number, ih: number) => {
            const el = containerRef.current;
            if (!el) return;
            const cw = el.clientWidth;
            const ch = el.clientHeight;
            if (!cw || !ch) return;
            const scale = Math.max(cw / iw, ch / ih);
            const dispW = iw * scale;
            const dispH = ih * scale;
            const fx = focal.x * dispW;
            const fy = focal.y * dispH;
            const tx = cw * target.x;
            const ty = ch * target.y;
            let ox = tx - fx;
            let oy = ty - fy;
            // Clamp so image always covers container and we don't over-shift
            const minX = cw - dispW;
            const minY = ch - dispH;
            ox = Math.min(0, Math.max(minX, ox));
            oy = Math.min(0, Math.max(minY, oy));
            setBgPos(`${ox}px ${oy}px`);
        };

        const loadAndCalc = () => {
            imgEl = new Image();
            imgEl.src = currentImage;
            if (imgEl.complete && imgEl.naturalWidth) {
                recalc(imgEl.naturalWidth, imgEl.naturalHeight);
            } else {
                imgEl.onload = () => recalc(imgEl!.naturalWidth, imgEl!.naturalHeight);
            }
        };

        const handleResize = () => {
            if (!imgEl || !imgEl.naturalWidth) return;
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => recalc(imgEl!.naturalWidth, imgEl!.naturalHeight));
        };

        loadAndCalc();
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [currentImage, focal.x, focal.y, target.x, target.y]);

    return (
        <PageTransition>
            <Box ref={containerRef} sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                // Use tablet asset through md (900-1199), desktop from lg+
                backgroundImage: {
                    xs: 'url(/jcfStandingLanding-mobile.jpg)',     // Mobile portrait image
                    sm: 'url(/jcfStandingLanding-tablet.jpg)',     // Tablet image (applies up to <1200px)
                    lg: 'url(/jcfStandingLanding-desktop.jpg)'     // Desktop image from 1200px+
                },
                backgroundSize: 'cover',
                backgroundPosition: bgPos,
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                // Swap to dedicated mobile-landscape asset when phones are rotated
                '@media (orientation: landscape) and (max-width: 1023px)': {
                    backgroundImage: 'url(/jcfStandingLanding-mobile-landscape.jpg)',
                    // Position is computed dynamically via bgPos
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: {
                        xs: 'linear-gradient(to bottom, rgba(15, 23, 42, 0.05) 0%, rgba(15, 23, 42, 0.1) 30%, rgba(15, 23, 42, 0.4) 70%, rgba(15, 23, 42, 0.5) 100%)', // Mobile: darker at bottom for text
                        md: 'linear-gradient(to right, rgba(15, 23, 42, 0.05) 0%, rgba(15, 23, 42, 0.1) 30%, rgba(15, 23, 42, 0.3) 70%, rgba(15, 23, 42, 0.45) 100%)' // Desktop: darker on right for text
                    },
                    zIndex: 1,
                    // On small landscape, darken the right side for better contrast
                    '@media (orientation: landscape) and (max-width: 1023px)': {
                        background: 'linear-gradient(to right, rgba(15, 23, 42, 0.05) 0%, rgba(15, 23, 42, 0.1) 35%, rgba(15, 23, 42, 0.35) 70%, rgba(15, 23, 42, 0.5) 100%)'
                    }
                }
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-end', sm: 'center', md: 'center' }, // Bottom on mobile, center on tablet/desktop
                        minHeight: '80vh',
                        // Center content up to md; shift right from lg+
                        justifyContent: { xs: 'center', md: 'center', lg: 'flex-end' },
                        textAlign: { xs: 'center', md: 'center', lg: 'left' },
                        pb: { xs: 6, sm: 4, md: 2, lg: 0 }, // Slight padding on md
                        // On small landscape, shift content to the right
                        '@media (orientation: landscape) and (max-width: 1023px)': {
                            justifyContent: 'flex-end',
                            textAlign: 'right',
                            alignItems: 'center',
                            pb: 2
                        }
                    }}>
                        <Box sx={{ 
                            // Use full width up to md; constrain from lg+
                            maxWidth: { xs: '100%', lg: '50%' },
                            ml: { lg: 'auto' },
                            pr: { lg: 4 },
                            '@media (orientation: landscape) and (max-width: 1023px)': {
                                maxWidth: '60%',
                                pr: 3
                            }
                        }}>
                            <Typography
                                variant="h1"
                                gutterBottom
                                sx={{
                                    mb: 3,
                                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                                    fontWeight: 'bold',
                                    color: '#f1f5f9'
                                }}
                            >
                                Hey there, I'm{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        background: 'linear-gradient(45deg, #3b82f6, #06b6d4)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        display: 'inline-block'
                                    }}
                                >
                                    John Fantell
                                </Box>
                            </Typography>
                            <Typography variant="h5" paragraph sx={{ 
                                mb: 4, 
                                fontSize: { xs: '1.2rem', md: '1.5rem' },
                                color: '#f1f5f9',
                                maxWidth: '600px'
                            }}>
                            </Typography>
                            <Box sx={{ 
                                display: 'flex', 
                                gap: 2, 
                                justifyContent: { xs: 'center', lg: 'flex-start' },
                                flexWrap: 'wrap' 
                            }}>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    component={Link} 
                                    to="/about"
                                    size="large"
                                    sx={{
                                        background: 'linear-gradient(45deg, #3b82f6, #06b6d4)',
                                        minWidth: '180px',
                                        '&:hover': {
                                            background: 'linear-gradient(45deg, #2563eb, #0891b2)',
                                        }
                                    }}
                                >
                                    MORE ABOUT ME
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </PageTransition>
    );
};

export default Home;
